import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, ApiError } from '@/api/client';
import type { Page } from '@/api/types';
import { Icon } from '@/components/Icon';
import { money, num, qty } from '@/lib/format';
import { receivePurchaseOrder } from './api';
import type { PurchaseOrderDetail, WarehouseStock } from './types';
import s from './PurchaseReceiveDialog.module.css';

export function PurchaseReceiveDialog({ order, canReadInventory, onClose, onSuccess }: {
  order: PurchaseOrderDetail;
  canReadInventory: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const [stocks, setStocks] = useState<WarehouseStock[]>([]);
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!canReadInventory) return;
    const controller = new AbortController();
    const productIds = [...new Set(order.lines.map((line) => line.productId))];
    void Promise.all(productIds.map((productId) => api.get<Page<WarehouseStock>>('/api/inventory/stocks', { warehouseId: order.warehouse.id, productId, pageSize: 1 }, controller.signal)))
      .then((pages) => { if (!controller.signal.aborted) setStocks(pages.flatMap((page) => page.items)); })
      .catch(() => undefined);
    return () => controller.abort();
  }, [canReadInventory, order.lines, order.warehouse.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const pendingLines = order.lines.filter((line) => num(line.quantity) - num(line.receivedQuantity) > 0.0001);
  const stockByProduct = useMemo(() => new Map(stocks.map((stock) => [stock.productId, num(stock.availableQuantity)])), [stocks]);
  const addedByProduct = new Map<string, number>();
  for (const line of pendingLines) {
    addedByProduct.set(line.productId, (addedByProduct.get(line.productId) || 0) + Number(quantities[line.id] || 0));
  }
  const total = pendingLines.reduce((sum, line) => sum + Number(quantities[line.id] || 0), 0);
  const value = pendingLines.reduce((sum, line) => sum + Number(quantities[line.id] || 0) * num(line.unitCost), 0);
  const invalid = pendingLines.some((line) => {
    const count = Number(quantities[line.id] || 0);
    return !Number.isFinite(count) || count < 0 || count - (num(line.quantity) - num(line.receivedQuantity)) > 0.0001;
  });

  function change(id: string, value: number) {
    setQuantities((current) => ({ ...current, [id]: value }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving || uncertain || invalid || total <= 0) return;
    setSaving(true);
    setError('');
    try {
      await receivePurchaseOrder(order.id, {
        lines: pendingLines.filter((line) => Number(quantities[line.id] || 0) > 0).map((line) => ({ lineId: line.id, quantity: Number(quantities[line.id]) })),
        note: note.trim() || undefined,
      });
      setDone(true);
      onSuccess();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '入库失败，请刷新采购单后重试');
    } finally {
      setSaving(false);
    }
  }

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={`采购单转入库 · ${order.purchaseNo}`}>
      <header className={s.head}><button type="button" className={s.phoneCancel} onClick={onClose} disabled={saving}>取消</button><h2>采购单转入库 <small>{order.purchaseNo}</small></h2><button type="button" className={s.close} onClick={onClose} disabled={saving} aria-label="关闭"><Icon name="x" size={18} /></button></header>
      {done ? <div className={s.success}><Icon name="check" size={24} /><h3>采购入库完成</h3><p>V1 已更新采购单和库存流水，当前页面会重新读取结果。</p><button type="button" onClick={onClose}>查看采购单</button></div> :
        <form onSubmit={(event) => void submit(event)}>
          <div className={s.body}>
            <div className={s.info}><div><span>供应商</span><b>{order.supplier.name}</b></div><div><span><i>*</i>入库仓库</span><b>{order.warehouse.name}</b></div><div><span>入库时间</span><b>以确认提交时间为准</b></div></div>
            <p className={s.hint}>核对实收数量后确认入库；系统会记录采购入库流水，并按采购单所属仓库更新库存。</p>
            <div className={s.tableWrap}><table className={s.table}><thead><tr><th>产品</th><th>采购量</th><th>已入库</th><th>本次入库</th><th>入库单价</th><th>入库后可用</th></tr></thead><tbody>
              {pendingLines.map((line) => {
                const pending = num(line.quantity) - num(line.receivedQuantity);
                const selected = Number(quantities[line.id] || 0);
                const available = stockByProduct.get(line.productId);
                const pack = num(line.unitsPerPackage);
                return <tr key={line.id}>
                  <td><b>{line.product.name}</b><small><span>{line.product.code}</span>{pack > 0 && <span className={s.packDetails}> · {qty(pack)} 个 / {line.packagingOption?.packageUnit || '件'}</span>}</small></td>
                  <td data-label="采购量">{qty(line.quantity)}</td><td data-label="已入库">{qty(line.receivedQuantity)}</td>
                  <td><div className={s.step}><button type="button" aria-label={`${line.product.name}减少一包装`} onClick={() => change(line.id, Math.max(0, selected - (pack > 0 ? pack : 1)))}>−</button><input type="number" min="0" max={pending} step="0.001" value={quantities[line.id] ?? 0} aria-label={`${line.product.name}本次入库数量`} onChange={(event) => change(line.id, Number(event.target.value))} /><button type="button" aria-label={`${line.product.name}增加一包装`} onClick={() => change(line.id, Math.min(pending, selected + (pack > 0 ? pack : 1)))}>＋</button></div><small>未入 {qty(pending)}</small></td>
                  <td data-label="入库单价">{line.unitCost == null ? '—' : money(line.unitCost)}</td>
                  <td data-label="入库后可用">{available == null ? '—' : <><span className={available < 0 ? s.beforeNegative : undefined}>{qty(available)}</span> → <b className={available + (addedByProduct.get(line.productId) || 0) >= 0 ? s.afterPositive : s.afterNegative}>{qty(available + (addedByProduct.get(line.productId) || 0))}</b></>}<small>{available == null ? '无库存查看权限或未建立库存行' : '当前可用 → 预计可用'}</small></td>
                </tr>;
              })}
            </tbody></table></div>
            {total > 0 && <div className={s.impactNotice}><Icon name="info" size={16} /><span>确认入库后，系统会重新计算可用库存及受影响订单的配货状态；达到缺货恢复条件时，会提醒相关跟单员。</span></div>}
            {!pendingLines.length && <p className={s.hint}>这张采购单已无待入库数量。</p>}
            {invalid && <p className={s.error} role="alert">本次入库数量不能为负数，也不能超过每行未入库数量。</p>}
            <label className={s.note}>入库备注<textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="选填，例如批次号、质检结果" /></label>
            {error && <p className={s.error} role="alert">{error}{uncertain && '。请求结果未确认，请先核对入库流水，暂勿重复提交。'}</p>}
          </div>
          <footer className={s.foot}><span>本次入库 <b>{qty(total)}</b> 件{order.totalAmount !== undefined && ` · 货值 ${money(value)}`}</span><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="submit" className={s.submit} disabled={saving || uncertain || invalid || total <= 0}>{saving ? '正在入库…' : '确认入库'}</button></footer>
        </form>}
    </section>
  </div>;
}
